import { DASHBOARD_QUERY_KEYS } from '@/entities/dashboard'
import type { ApiResponse, PaginatedResponse } from '@/shared/api/types/api-response'
import type {
  CreatePoiCategoryPayload,
  CreatePoiPayload,
  IPOI,
  IPoiAudio,
  IPoiCategory,
  PoiCategoryListParams,
  PoiListParams,
  UpdatePoiCategoryPayload,
  UpdatePoiPayload,
} from '@/shared/api/types/poi-types'
import type { TranslationStatusType } from '@/shared/constants'
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  bulkUpdateTranslationStatus,
  createPoi,
  createPoiCategory,
  deletePoi,
  deletePoiCategory,
  generatePoiTts,
  getPoiById,
  getPoiCategories,
  getPoiCategoryById,
  getPois,
  getPoisSuperadmin,
  getPoiTypes,
  reorderPoiImages,
  updatePoi,
  updatePoiCategory,
  updateTranslationStatus,
  uploadPoiAudio,
  uploadPoiImage,
} from '@/shared/api/services/poi-service'
import { getNextPaginatedPageParam } from '@/shared/api/utils/pagination'
import { AdminScopeType } from '@/shared/constants'

type PoiQueryScope = AdminScopeType.Tenant | AdminScopeType.Super

export const POI_QUERY_KEYS = {
  all: ['pois'] as const,
  listPrefix: () => [...POI_QUERY_KEYS.all, 'list'] as const,
  list: (scope: PoiQueryScope, tenantId: string, params: PoiListParams) =>
    [...POI_QUERY_KEYS.all, 'list', scope, tenantId, params] as const,
  categoriesPrefix: () => [...POI_QUERY_KEYS.all, 'categories'] as const,
  categories: (tenantId: string, params: PoiCategoryListParams) =>
    [...POI_QUERY_KEYS.categoriesPrefix(), tenantId, params] as const,
  categoryDetailPrefix: (id: string) => [...POI_QUERY_KEYS.all, 'category-detail', id] as const,
  categoryDetail: (id: string, languageCode?: string | null) =>
    [...POI_QUERY_KEYS.all, 'category-detail', id, languageCode ?? 'default'] as const,
  types: (languageCode?: string | null) =>
    [...POI_QUERY_KEYS.all, 'types', languageCode ?? 'default'] as const,
  detailPrefix: (id: string) => [...POI_QUERY_KEYS.all, 'detail', id] as const,
  detail: (id: string, languageCode?: string | null) =>
    [...POI_QUERY_KEYS.all, 'detail', id, languageCode ?? 'default'] as const,
}

/**
 * Fetches tenant-scoped POIs with the provided pagination, filter, and language parameters.
 */
export function useTenantPoisQuery(
  tenantId: string,
  params: PoiListParams,
  options?: { enabled?: boolean },
) {
  return useQuery<PaginatedResponse<IPOI>, Error>({
    queryKey: POI_QUERY_KEYS.list(AdminScopeType.Tenant, tenantId, params),
    queryFn: async (): Promise<PaginatedResponse<IPOI>> => getPois(params),
    enabled: !!tenantId && (options?.enabled ?? true),
    placeholderData: keepPreviousData,
  })
}

export function useTenantPoisInfiniteQuery(
  tenantId: string,
  params: Omit<PoiListParams, 'current'> & { pageSize: number },
  options?: { enabled?: boolean },
) {
  return useInfiniteQuery<PaginatedResponse<IPOI>, Error>({
    queryKey: [
      ...POI_QUERY_KEYS.list(AdminScopeType.Tenant, tenantId, { ...params, current: 1 }),
      'infinite',
    ],
    queryFn: async ({ pageParam }) => getPois({ ...params, current: pageParam as number }),
    initialPageParam: 1,
    getNextPageParam: getNextPaginatedPageParam,
    enabled: !!tenantId && (options?.enabled ?? true),
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

/**
 * Fetches superadmin POIs while keeping the query cache separated by tenant, filters, and scope.
 */
export function useSuperadminPoisQuery(
  tenantId: string,
  params: PoiListParams,
  options?: { enabled?: boolean },
) {
  return useQuery<PaginatedResponse<IPOI>, Error>({
    queryKey: POI_QUERY_KEYS.list(AdminScopeType.Super, tenantId, params),
    queryFn: async (): Promise<PaginatedResponse<IPOI>> => getPoisSuperadmin(params),
    enabled: !!tenantId && (options?.enabled ?? true),
    staleTime: 0,
    refetchOnMount: 'always',
    placeholderData: keepPreviousData,
  })
}

/**
 * Fetches a POI detail record, optionally localized by the requested language code.
 */
export function usePoiByIdQuery(id: string, languageCode?: string | null) {
  return useQuery({
    queryKey: POI_QUERY_KEYS.detail(id, languageCode),
    queryFn: async () => getPoiById(id, languageCode),
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

/**
 * Fetches localized POI categories for the active tenant.
 */
export function usePoiCategoriesQuery(
  tenantId: string,
  params: PoiCategoryListParams = {},
  options?: { enabled?: boolean },
) {
  return useQuery<PaginatedResponse<IPoiCategory>, Error>({
    queryKey: POI_QUERY_KEYS.categories(tenantId, params),
    queryFn: async (): Promise<PaginatedResponse<IPoiCategory>> => getPoiCategories(params),
    enabled: !!tenantId && (options?.enabled ?? true),
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export function usePoiCategoriesInfiniteQuery(
  tenantId: string,
  params: Omit<PoiCategoryListParams, 'current'> & { pageSize: number },
  options?: { enabled?: boolean },
) {
  return useInfiniteQuery<PaginatedResponse<IPoiCategory>, Error>({
    queryKey: [...POI_QUERY_KEYS.categories(tenantId, params), 'infinite'],
    queryFn: async ({ pageParam }) => getPoiCategories({ ...params, current: pageParam as number }),
    initialPageParam: 1,
    getNextPageParam: getNextPaginatedPageParam,
    enabled: !!tenantId && (options?.enabled ?? true),
  })
}

export function usePoiCategoryByIdQuery(
  id: string,
  languageCode?: string | null,
  options?: { enabled?: boolean },
) {
  return useQuery<ApiResponse<IPoiCategory>, Error>({
    queryKey: POI_QUERY_KEYS.categoryDetail(id, languageCode),
    queryFn: async () => getPoiCategoryById(id, languageCode),
    enabled: !!id && (options?.enabled ?? true),
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

/**
 * Creates a POI category and refreshes cached category lists.
 */
export function useCreatePoiCategoryMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CreatePoiCategoryPayload) => createPoiCategory(payload),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: POI_QUERY_KEYS.categoriesPrefix() }),
        queryClient.invalidateQueries({ queryKey: POI_QUERY_KEYS.listPrefix() }),
      ])
    },
  })
}

/**
 * Updates a POI category and refreshes cached category lists.
 */
export function useUpdatePoiCategoryMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdatePoiCategoryPayload }) =>
      updatePoiCategory(id, payload),
    onSuccess: async (_data, { id }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: POI_QUERY_KEYS.categoriesPrefix() }),
        queryClient.invalidateQueries({ queryKey: POI_QUERY_KEYS.categoryDetailPrefix(id) }),
        queryClient.invalidateQueries({ queryKey: POI_QUERY_KEYS.listPrefix() }),
      ])
      await queryClient.refetchQueries({
        queryKey: POI_QUERY_KEYS.categoriesPrefix(),
        type: 'active',
      })
    },
  })
}

/**
 * Deletes a POI category and refreshes cached category lists.
 */
export function useDeletePoiCategoryMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => deletePoiCategory(id),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: POI_QUERY_KEYS.categoriesPrefix() }),
        queryClient.invalidateQueries({ queryKey: POI_QUERY_KEYS.listPrefix() }),
      ])
    },
  })
}

/**
 * Fetches localized public POI type labels.
 */
export function usePoiTypesQuery(languageCode?: string | null) {
  return useQuery<ApiResponse<string[]>, Error>({
    queryKey: POI_QUERY_KEYS.types(languageCode),
    queryFn: async (): Promise<ApiResponse<string[]>> => getPoiTypes(languageCode),
  })
}

/**
 * Creates a POI and invalidates list and detail caches that may include the new record.
 */
export function useCreatePoiMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CreatePoiPayload) => createPoi(payload),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: POI_QUERY_KEYS.all }),
        queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.all }),
      ])
    },
  })
}

/**
 * Updates a POI and invalidates every localized detail cache for that POI.
 */
export function useUpdatePoiMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdatePoiPayload }) =>
      updatePoi(id, payload),
    onSuccess: async (_data, { id }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: POI_QUERY_KEYS.listPrefix() }),
        queryClient.invalidateQueries({ queryKey: POI_QUERY_KEYS.detailPrefix(id) }),
        queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.all }),
      ])
    },
  })
}

/**
 * Deletes a POI and refreshes cached POI lists.
 */
export function useDeletePoiMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => deletePoi(id),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: POI_QUERY_KEYS.all }),
        queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.all }),
      ])
    },
  })
}

/**
 * Uploads or replaces a POI image and refreshes related POI caches.
 */
export function useUploadPoiImagesMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, imageFile, index }: { id: string; imageFile: File; index?: number }) =>
      uploadPoiImage(id, imageFile, index),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: POI_QUERY_KEYS.all })
      void queryClient.invalidateQueries({ queryKey: POI_QUERY_KEYS.detailPrefix(id) })
    },
  })
}

/**
 * Persists a new image order for a POI and refreshes related POI caches.
 */
export function useReorderPoiImagesMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, newIndex }: { id: string; newIndex: number[] }) =>
      reorderPoiImages(id, newIndex),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: POI_QUERY_KEYS.all })
      void queryClient.invalidateQueries({ queryKey: POI_QUERY_KEYS.detailPrefix(id) })
    },
  })
}

/**
 * Starts TTS generation for a POI language and refreshes localized detail data.
 */
export function useGeneratePoiTtsMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, language }: { id: string; language: string }) =>
      generatePoiTts(id, language),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: POI_QUERY_KEYS.detailPrefix(id) })
    },
  })
}

/**
 * Uploads POI audio for a language and refreshes localized detail data.
 */
export function useUploadPoiAudioMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, audio }: { id: string; audio: Omit<IPoiAudio, 'id'> }) =>
      uploadPoiAudio(id, audio),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: POI_QUERY_KEYS.detailPrefix(id) })
    },
  })
}

/**
 * Updates one POI translation status and refreshes list and detail caches.
 */
export function useUpdatePoiTranslationStatusMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      poiId,
      language,
      status,
    }: {
      poiId: string
      language: string
      status: TranslationStatusType
    }) => updateTranslationStatus(poiId, language, status),
    onSuccess: (_data, { poiId }) => {
      void queryClient.invalidateQueries({ queryKey: POI_QUERY_KEYS.all })
      void queryClient.invalidateQueries({ queryKey: POI_QUERY_KEYS.detailPrefix(poiId) })
    },
  })
}

/**
 * Updates translation status for multiple POIs and refreshes cached POI lists.
 */
export function useBulkUpdatePoiTranslationStatusMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      poiIds,
      language,
      status,
    }: {
      poiIds: string[]
      language: string
      status: TranslationStatusType
    }) => bulkUpdateTranslationStatus(poiIds, language, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: POI_QUERY_KEYS.all })
    },
  })
}
